// Channel references in message text. People type "#general"; the room stores "<#channelId>" (like Discord), so a
// reference keeps pointing at the right channel after a rename. Clients show it as "#<current name>".
// Inside `code` a "#name" stays text.
import { outsideCode } from "./markdown.ts";

/** A stored reference: "<#" + channel id + ">". */
export const CHANNEL_TOKEN = /<#([0-9A-Za-z]{10,40})>/g;

// "#name" at the start or after a space or opening punctuation (so "C#" and URL fragments don't count).
const TYPED_REF = /(^|[\s(\[{"'])#([a-z0-9][a-z0-9-]*)/gi;

/** Turns typed "#name" into "<#id>" for channels that exist. byName: lowercase name → id. */
export function encodeChannelRefs(text: string, byName: Map<string, string>): string {
  if (!text.includes("#")) return text;
  return outsideCode(text, (seg) => seg.replace(TYPED_REF, (whole, lead: string, raw: string) => {
    // Trailing dashes are punctuation ("see #general-").
    const name = raw.replace(/-+$/, "").toLowerCase();
    const id = byName.get(name);
    return id ? `${lead}<#${id}>${raw.slice(name.length)}` : whole;
  }));
}

/** Turns "<#id>" back into "#name" (for editing, excerpts and anything shown as plain text). */
export function decodeChannelRefs(text: string, byId: Map<string, string>): string {
  if (!text.includes("<#")) return text;
  return text.replace(CHANNEL_TOKEN, (_, id: string) => `#${byId.get(id) ?? "deleted-channel"}`);
}

/**
 * May someone with this role create channels and categories (and rename channels)? Owners and admins. The room checks
 * it on every request; the clients use it to decide whether to show the + buttons. A role nobody knows can't.
 */
export function canManageChannels(role: string | undefined): boolean {
  return role === "owner" || role === "admin";
}
